import { ItemTypeMeta } from "./item"
import { ItemTypes } from "./item_type"

/**
 * How the crafting panel divides its recipes up.
 *
 * The tabs used to be the item's `class` -- "resource", "structure", "weapon" -- which is the
 * engine's taxonomy, not a player's. It filed a rabbit snare between a roof and a door because both
 * are structures, and arrows next to planks because an arrow is a resource. From a playtest: "it took
 * me a bit to find the tab to craft sticks", and "I didn't see a way to craft arrows".
 *
 * These groups answer "what am I trying to do" instead:
 *
 *  - CAMP      the things a camp is built out of. What a player opens this screen for on day one.
 *  - HUNTING   the kit for catching things: bow, arrows, snare. Scattered across all three old tabs.
 *  - WEAPONS   what you fight the waves with.
 *  - MATERIALS the intermediates the rest of the list is made of. A step, never a goal, so it is last.
 *
 * The order is the order they are offered in, and it is DECLARED rather than derived: the old tab
 * order fell out of the order recipes happened to appear in `itemRecipes`, so adding a recipe could
 * silently move a tab.
 */
export const CRAFTING_GROUPS = ["camp", "hunting", "weapons", "materials"] as const
export type CraftingGroup = (typeof CRAFTING_GROUPS)[number]

export const CRAFTING_GROUP_LABELS: Record<CraftingGroup, string> = {
    camp: "Camp",
    hunting: "Hunting",
    weapons: "Weapons",
    materials: "Materials",
}

/**
 * The recipes whose group cannot be read off their class.
 *
 * Three items, and each one is a case where the engine's filing and the player's expectation come
 * apart. An `arrow` is class `resource` (it stacks, it is consumed) but nobody looks for arrows under
 * materials. A `snare-rabbit` is class `structure` (it is placed in the world) but it is hunting gear,
 * not architecture. A `bow` is class `weapon`, and it is -- but it belongs beside the arrows it fires.
 *
 * Kept as overrides on top of the class rule rather than a full table, so a new recipe lands somewhere
 * sensible without anyone having to remember to add it here.
 */
const GROUP_OVERRIDES: Partial<Record<ItemTypes, CraftingGroup>> = {
    arrow: "hunting",
    "ember-charge": "weapons",
    "snare-rabbit": "hunting",
    bow: "hunting",
}

const GROUP_BY_CLASS: Record<string, CraftingGroup> = {
    structure: "camp",
    weapon: "weapons",
    resource: "materials",
}

export const craftingGroupFor = (itemTypeMeta: ItemTypeMeta): CraftingGroup =>
    GROUP_OVERRIDES[itemTypeMeta.type as ItemTypes] ?? GROUP_BY_CLASS[itemTypeMeta.class] ?? "materials"

/**
 * Where a material that has NO recipe comes from.
 *
 * The crafting tree bottoms out in things you go and get, and the panel used to simply stop there: a
 * rope needs straw, and a player who has never seen straw drop had no way to learn that cutting long
 * grass is the answer. A greyed row is only a goal if its cost is reachable, so the leaves have to
 * say where they are.
 *
 * Only materials that are actually an INGREDIENT of something need an entry -- anything else is never
 * displayed as a shortfall. Checked against the recipe list by crafting_sources.test.ts, so a new
 * recipe reaching for a new raw material fails the build rather than drawing a blank.
 */
export const ITEM_SOURCE_HINTS: Partial<Record<ItemTypes, string>> = {
    wood: "Chop trees",
    straw: "Cut long grass",
    feather: "Hunt chickens",
    "shard-metal": "Dropped by snakes and slimes",
    iron: "Mine ore veins",
    silver: "Mine ore veins",
    copper: "Mine ore veins",
    gold: "Mine ore veins",
    coal: "Mine ore veins",
    "spider-silk": "Hunt spiders outside camp",
}
